import React from "react";
import {Row, Col} from "react-bootstrap";
import {StyleCard} from "components/Common/StyleCard";
import {StyleDiv} from "components/Common/StyleDiv";
import {ws} from "styles/tokens";
import {isAndroid} from "FirebaseUtils";
import "styles/css/Skeleton.css";

interface ActivityPageSkeletonProps {
  isWebview?: boolean;
  isDarkMode?: boolean;
}

function ActivityPageSkeleton({isWebview = false, isDarkMode = false}: ActivityPageSkeletonProps) {
  // The shimmer highlight must be lighter than the block in both themes, so the two token pairs differ.
  const backgroundColor = isDarkMode ? ws.field : ws.line;
  const foregroundColor = isDarkMode ? ws.line : ws.wash;
  const cardBackgroundColor = isAndroid && !isDarkMode ? ws.wash : ws.card;
  const skeletonVars = {
    "--skeleton-bg": backgroundColor,
    "--skeleton-fg": foregroundColor,
    "--skeleton-duration": "1.4s"
  };

  const SkeletonBlock = ({
    width,
    height,
    radius,
    margin,
    style
  }: {
    width?: string | number;
    height?: string | number;
    radius?: string | number;
    margin?: string | number;
    style?: any;
  }) => (
    <StyleDiv
      className="skeleton-block"
      width={width}
      height={height}
      borderRadius={radius}
      margin={margin}
      style={style}
    />
  );

  const isWebviewLayout = isWebview || isAndroid;
  const cardsCount = 1;

  return (
    <StyleDiv>
      {!isWebviewLayout && (
        <Row className="g-0">
          <Col md={{offset: 1, span: 10}} className="pl-3 pb-3">
            <StyleCard padding="12px">
              <StyleDiv className="skeleton-container" style={skeletonVars} display="flex" gap="20px">
                <SkeletonBlock width="120px" height="28px" radius="6px" />
                <SkeletonBlock width="120px" height="28px" radius="6px" />
                <SkeletonBlock width="120px" height="28px" radius="6px" />
                <SkeletonBlock width="120px" height="28px" radius="6px" />
              </StyleDiv>
            </StyleCard>
          </Col>
        </Row>
      )}
      {Array.from({length: cardsCount}).map((_, index) => (
        <Row key={`activity-skeleton-${index}`} className="g-0">
          <Col
            md={isWebviewLayout ? {offset: 0, span: 12} : {offset: 1, span: 10}}
            className={isWebviewLayout ? "pb-3" : "pr-3 pb-3 pl-3"}
          >
            <StyleCard
              backgroundColor={isWebviewLayout ? "transparent" : cardBackgroundColor}
              padding={isWebviewLayout ? "16px" : "1rem"}
              boxShadow={isWebviewLayout ? "none" : ws.shadowSoft}
              border={isWebviewLayout ? "none" : undefined}
              margin={isAndroid ? "0 10px" : undefined}
            >
              <StyleDiv className="skeleton-container" style={skeletonVars} padding="0">
                <SkeletonBlock width="100%" height="250px" radius="16px" margin="0 0 24px 0" />
                <StyleDiv display="flex" gap="60px" flexWrap="wrap" margin="0 0 24px 0">
                  <StyleDiv>
                    <SkeletonBlock width="150px" height="18px" radius="7px" margin="0 0 8px 0" />
                    <SkeletonBlock width="130px" height="30px" radius="7px" />
                  </StyleDiv>
                  <StyleDiv>
                    <SkeletonBlock width="150px" height="18px" radius="7px" margin="0 0 8px 0" />
                    <SkeletonBlock width="130px" height="30px" radius="7px" />
                  </StyleDiv>
                  <StyleDiv>
                    <SkeletonBlock width="150px" height="18px" radius="7px" margin="0 0 8px 0" />
                    <SkeletonBlock width="130px" height="30px" radius="7px" />
                  </StyleDiv>
                  <StyleDiv>
                    <SkeletonBlock width="150px" height="18px" radius="7px" margin="0 0 8px 0" />
                    <SkeletonBlock width="130px" height="30px" radius="7px" />
                  </StyleDiv>
                </StyleDiv>
                <SkeletonBlock width="360px" height="16px" radius="8px" margin="0 0 12px 0" />
                <SkeletonBlock width="360px" height="16px" radius="8px" margin="0 0 12px 0" />
                <SkeletonBlock width="300px" height="16px" radius="8px" />
              </StyleDiv>
            </StyleCard>
          </Col>
        </Row>
      ))}
    </StyleDiv>
  );
}

export default ActivityPageSkeleton;
